import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  listReviewCycles, createReviewCycle, updateReviewCycle, deleteReviewCycle,
  listPerformanceReviews, createPerformanceReview, updatePerformanceReview,
  submitSelfAssessment, submitManagerReview, completePerformanceReview,
  acknowledgePerformanceReview,
  listPerformanceGoals, createPerformanceGoal, updatePerformanceGoal, deletePerformanceGoal,
} from '../api';
import { useConfirm } from './ConfirmProvider';

const CYCLE_STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-600',
  ACTIVE: 'bg-green-100 text-green-700',
  COMPLETED: 'bg-blue-100 text-blue-700',
  ARCHIVED: 'bg-slate-100 text-slate-600',
};

const REVIEW_STATUS_COLORS = {
  PENDING_SELF: 'bg-yellow-100 text-yellow-700',
  PENDING_MGR: 'bg-blue-100 text-blue-700',
  IN_DISCUSSION: 'bg-purple-100 text-purple-700',
  COMPLETED: 'bg-green-100 text-green-700',
  ACKNOWLEDGED: 'bg-teal-100 text-teal-700',
};

const GOAL_STATUS_OPTIONS = [
  { value: 'NOT_STARTED', label: 'Not Started' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ACHIEVED', label: 'Achieved' },
  { value: 'MISSED', label: 'Missed' },
  { value: 'EXCEEDED', label: 'Exceeded' },
];

const GOAL_TYPE_OPTIONS = [
  { value: 'BILLABLE_HOURS', label: 'Billable Hours' },
  { value: 'TASK_COMPLETION', label: 'Task Completion' },
  { value: 'CLIENT_SATISFACTION', label: 'Client Satisfaction' },
  { value: 'BUSINESS_DEV', label: 'Business Development' },
  { value: 'SKILLS', label: 'Skills & Training' },
  { value: 'CUSTOM', label: 'Custom' },
];

function PerformanceReviews({ authAxios, showFormMessage, userRole, currentUser, users }) {
  const confirm = useConfirm();
  const [activeSubTab, setActiveSubTab] = useState('cycles');
  const [cycles, setCycles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [goals, setGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedCycle, setSelectedCycle] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);

  // Forms
  const [showCycleForm, setShowCycleForm] = useState(false);
  const [editingCycle, setEditingCycle] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [showReviewDetail, setShowReviewDetail] = useState(null);
  const [showSelfAssessmentForm, setShowSelfAssessmentForm] = useState(false);
  const [showManagerReviewForm, setShowManagerReviewForm] = useState(false);
  const [inlineActual, setInlineActual] = useState({});

  const [cycleForm, setCycleForm] = useState({
    name: '', cycle_type: 'QUARTERLY', start_date: '', end_date: '', status: 'DRAFT',
  });
  const [reviewForm, setReviewForm] = useState({ employee: '', reviewer: '', review_cycle: '' });
  const [goalForm, setGoalForm] = useState({
    employee: '', review_cycle: '', title: '', description: '', goal_type: 'CUSTOM',
    target_value: '', unit: '', weight: '1.0', due_date: '',
  });
  const [selfAssessmentData, setSelfAssessmentData] = useState({ self_assessment: '', self_rating: '' });
  const [managerReviewData, setManagerReviewData] = useState({
    manager_assessment: '', manager_rating: '', overall_rating: '',
    strengths: '', areas_for_improvement: '', action_items: '',
  });

  const canManageCycles = useMemo(() =>
    ['PARTNER', 'SUPERUSER'].includes(userRole) || currentUser?.is_superuser,
    [userRole, currentUser]
  );

  /* ---- Fetch Data ---- */
  const fetchCycles = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listReviewCycles(authAxios);
      setCycles(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load review cycles.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage]);

  const fetchReviews = useCallback(async (cycleId) => {
    try {
      const params = cycleId ? { review_cycle: cycleId } : {};
      const data = await listPerformanceReviews(authAxios, params);
      setReviews(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load reviews.'); }
  }, [authAxios, showFormMessage]);

  const fetchGoals = useCallback(async (cycleId) => {
    try {
      const params = cycleId ? { review_cycle: cycleId } : {};
      const data = await listPerformanceGoals(authAxios, params);
      setGoals(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load goals.'); }
  }, [authAxios, showFormMessage]);

  useEffect(() => { fetchCycles(); }, [fetchCycles]);
  useEffect(() => { fetchReviews(selectedCycle?.id); fetchGoals(selectedCycle?.id); }, [selectedCycle, fetchReviews, fetchGoals]);

  /* ---- Cycle CRUD ---- */
  const resetCycleForm = () => {
    setCycleForm({ name: '', cycle_type: 'QUARTERLY', start_date: '', end_date: '', status: 'DRAFT' });
    setEditingCycle(null); setShowCycleForm(false);
  };
  const handleCycleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCycle) {
        await updateReviewCycle(authAxios, editingCycle.id, cycleForm);
        showFormMessage('success', 'Cycle updated.');
      } else {
        await createReviewCycle(authAxios, cycleForm);
        showFormMessage('success', 'Cycle created.');
      }
      resetCycleForm(); fetchCycles();
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to save cycle.'); }
  };
  const handleDeleteCycle = async (id) => {
    if (!(await confirm({ title: 'Delete Review Cycle', message: 'Delete this review cycle?', confirmText: 'Delete', variant: 'danger' }))) return;
    try { await deleteReviewCycle(authAxios, id); showFormMessage('success', 'Cycle deleted.'); fetchCycles(); }
    catch { showFormMessage('error', 'Failed to delete cycle.'); }
  };

  /* ---- Review CRUD ---- */
  const handleCreateReview = async (e) => {
    e.preventDefault();
    try {
      await createPerformanceReview(authAxios, reviewForm);
      showFormMessage('success', 'Review created.');
      setShowReviewForm(false);
      fetchReviews(selectedCycle?.id);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || err.response?.data?.non_field_errors?.[0] || 'Failed to create review.'); }
  };

  const handleSelfAssessmentSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitSelfAssessment(authAxios, selectedReview.id, selfAssessmentData);
      showFormMessage('success', 'Self-assessment submitted.');
      setShowSelfAssessmentForm(false);
      fetchReviews(selectedCycle?.id);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed.'); }
  };

  const handleManagerReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitManagerReview(authAxios, selectedReview.id, managerReviewData);
      showFormMessage('success', 'Manager review submitted.');
      setShowManagerReviewForm(false);
      fetchReviews(selectedCycle?.id);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed.'); }
  };

  const handleCompleteReview = async (id) => {
    try {
      await completePerformanceReview(authAxios, id);
      showFormMessage('success', 'Review completed.');
      fetchReviews(selectedCycle?.id);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed.'); }
  };

  const handleAcknowledgeReview = async (id) => {
    try {
      await acknowledgePerformanceReview(authAxios, id);
      showFormMessage('success', 'Review acknowledged.');
      fetchReviews(selectedCycle?.id);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed.'); }
  };

  /* ---- Goal CRUD ---- */
  const resetGoalForm = () => {
    setGoalForm({ employee: '', review_cycle: '', title: '', description: '', goal_type: 'CUSTOM', target_value: '', unit: '', weight: '1.0', due_date: '' });
    setEditingGoal(null);
    setShowGoalForm(false);
  };
  const handleGoalSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...goalForm };
      if (selectedCycle) payload.review_cycle = selectedCycle.id;
      if (!payload.due_date) delete payload.due_date;
      if (!payload.target_value) delete payload.target_value;
      if (editingGoal) {
        await updatePerformanceGoal(authAxios, editingGoal.id, payload);
        showFormMessage('success', 'Goal updated.');
      } else {
        await createPerformanceGoal(authAxios, payload);
        showFormMessage('success', 'Goal created.');
      }
      resetGoalForm(); fetchGoals(selectedCycle?.id);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to create goal.'); }
  };
  const handleDeleteGoal = async (id) => {
    if (!(await confirm({ title: 'Delete Goal', message: 'Delete this goal?', confirmText: 'Delete', variant: 'danger' }))) return;
    try { await deletePerformanceGoal(authAxios, id); showFormMessage('success', 'Goal deleted.'); fetchGoals(selectedCycle?.id); }
    catch { showFormMessage('error', 'Failed to delete goal.'); }
  };
  const handleGoalStatusUpdate = async (id, status) => {
    try { await updatePerformanceGoal(authAxios, id, { status }); fetchGoals(selectedCycle?.id); }
    catch {}
  };

  const handleEditGoal = (g) => {
    setGoalForm({
      employee: g.employee, review_cycle: g.review_cycle || '', title: g.title, description: g.description || '',
      goal_type: g.goal_type, target_value: g.target_value || '', unit: g.unit || '',
      weight: g.weight || '1.0', due_date: g.due_date || '',
    });
    setEditingGoal(g);
    setShowGoalForm(true);
  };

  const handleActualValueUpdate = async (id, value) => {
    try {
      await updatePerformanceGoal(authAxios, id, { actual_value: value });
      fetchGoals(selectedCycle?.id);
    } catch { showFormMessage('error', 'Failed to update progress.'); }
  };

  const staffUsers = useMemo(() =>
    (users || []).filter(u => u.is_staff || u.staff_profile),
    [users]
  );

  const subTabs = [
    { id: 'cycles', label: 'Review Cycles' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'goals', label: 'Goals' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Performance Reviews</h2>
          <p className="text-sm text-gray-500 mt-1">Manage review cycles, goals, and formal reviews</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setActiveSubTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeSubTab === t.id ? 'bg-white shadow text-blue-700' : 'text-gray-600 hover:text-gray-800'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Cycle selector if on reviews/goals tab */}
      {(activeSubTab === 'reviews' || activeSubTab === 'goals') && (
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-600">Review Cycle:</label>
            <select value={selectedCycle?.id || ''} onChange={e => {
              const c = cycles.find(c => c.id === parseInt(e.target.value));
              setSelectedCycle(c || null);
            }} className="border rounded-lg px-3 py-2 text-sm flex-1 max-w-xs">
              <option value="">All Cycles</option>
              {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {isLoading && <div className="text-center py-8 text-gray-500">Loading...</div>}

      {/* ==================== CYCLES TAB ==================== */}
      {activeSubTab === 'cycles' && !isLoading && (
        <>
          {canManageCycles && (
            <div className="mb-4">
              <button onClick={() => { resetCycleForm(); setShowCycleForm(true); }}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition">
                + New Review Cycle
              </button>
            </div>
          )}

          {showCycleForm && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">{editingCycle ? 'Edit Cycle' : 'New Review Cycle'}</h3>
                  <form onSubmit={handleCycleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input type="text" required value={cycleForm.name}
                        onChange={e => setCycleForm(p => ({ ...p, name: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="e.g. Q1 2026 Review" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <select value={cycleForm.cycle_type}
                          onChange={e => setCycleForm(p => ({ ...p, cycle_type: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full">
                          <option value="QUARTERLY">Quarterly</option>
                          <option value="SEMI_ANNUAL">Semi-Annual</option>
                          <option value="ANNUAL">Annual</option>
                          <option value="AD_HOC">Ad Hoc</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select value={cycleForm.status}
                          onChange={e => setCycleForm(p => ({ ...p, status: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full">
                          <option value="DRAFT">Draft</option>
                          <option value="ACTIVE">Active</option>
                          <option value="COMPLETED">Completed</option>
                          <option value="ARCHIVED">Archived</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                        <input type="date" required value={cycleForm.start_date}
                          onChange={e => setCycleForm(p => ({ ...p, start_date: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                        <input type="date" required value={cycleForm.end_date}
                          onChange={e => setCycleForm(p => ({ ...p, end_date: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full" />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={resetCycleForm}
                        className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                      <button type="submit"
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700">
                        {editingCycle ? 'Update' : 'Create Cycle'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {cycles.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
              No review cycles created yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {cycles.map(c => (
                <div key={c.id} className="bg-white rounded-xl shadow-sm border p-5 hover:shadow-md transition">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800">{c.name}</h4>
                      <p className="text-xs text-gray-500 mt-0.5">{c.cycle_type_display || c.cycle_type}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CYCLE_STATUS_COLORS[c.status] || ''}`}>
                      {c.status_display || c.status}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>📅 {new Date(c.start_date).toLocaleDateString()} — {new Date(c.end_date).toLocaleDateString()}</p>
                    <p>📝 {c.review_count || 0} reviews</p>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => { setSelectedCycle(c); setActiveSubTab('reviews'); }}
                      className="text-xs text-blue-600 hover:underline">View Reviews</button>
                    {canManageCycles && (
                      <>
                        <button onClick={() => {
                          setCycleForm({ name: c.name, cycle_type: c.cycle_type, start_date: c.start_date, end_date: c.end_date, status: c.status });
                          setEditingCycle(c); setShowCycleForm(true);
                        }} className="text-xs text-gray-500 hover:underline">Edit</button>
                        <button onClick={() => handleDeleteCycle(c.id)}
                          className="text-xs text-red-500 hover:underline">Delete</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ==================== REVIEWS TAB ==================== */}
      {activeSubTab === 'reviews' && !isLoading && (
        <>
          {canManageCycles && selectedCycle && (
            <div className="mb-4">
              <button onClick={() => {
                setReviewForm({ employee: '', reviewer: '', review_cycle: selectedCycle.id });
                setShowReviewForm(true);
              }} className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition">
                + Create Review
              </button>
            </div>
          )}

          {/* Create Review Modal */}
          {showReviewForm && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Create Performance Review</h3>
                  <form onSubmit={handleCreateReview} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                      <select required value={reviewForm.employee}
                        onChange={e => setReviewForm(p => ({ ...p, employee: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full">
                        <option value="">Select employee</option>
                        {staffUsers.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.username})</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reviewer</label>
                      <select value={reviewForm.reviewer}
                        onChange={e => setReviewForm(p => ({ ...p, reviewer: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full">
                        <option value="">Auto (reports to)</option>
                        {staffUsers.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowReviewForm(false)}
                        className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                      <button type="submit"
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700">Create</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Self-assessment Modal */}
          {showSelfAssessmentForm && selectedReview && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Self-Assessment</h3>
                  <form onSubmit={handleSelfAssessmentSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Self-Assessment *</label>
                      <textarea rows={5} required value={selfAssessmentData.self_assessment}
                        onChange={e => setSelfAssessmentData(p => ({ ...p, self_assessment: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="Describe your performance during this review period..." />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Self-Rating (1-5) *</label>
                      <select required value={selfAssessmentData.self_rating}
                        onChange={e => setSelfAssessmentData(p => ({ ...p, self_rating: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full">
                        <option value="">Select</option>
                        {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} — {['Needs Improvement','Below Expectations','Meets Expectations','Exceeds Expectations','Outstanding'][n-1]}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowSelfAssessmentForm(false)}
                        className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                      <button type="submit"
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700">Submit</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Manager Review Modal */}
          {showManagerReviewForm && selectedReview && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Manager Review</h3>
                  <form onSubmit={handleManagerReviewSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Manager Assessment *</label>
                      <textarea rows={4} required value={managerReviewData.manager_assessment}
                        onChange={e => setManagerReviewData(p => ({ ...p, manager_assessment: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Manager Rating (1-5) *</label>
                        <select required value={managerReviewData.manager_rating}
                          onChange={e => setManagerReviewData(p => ({ ...p, manager_rating: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full">
                          <option value="">Select</option>
                          {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Overall Rating (1-5)</label>
                        <select value={managerReviewData.overall_rating}
                          onChange={e => setManagerReviewData(p => ({ ...p, overall_rating: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full">
                          <option value="">Select</option>
                          {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Strengths</label>
                      <textarea rows={2} value={managerReviewData.strengths}
                        onChange={e => setManagerReviewData(p => ({ ...p, strengths: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Areas for Improvement</label>
                      <textarea rows={2} value={managerReviewData.areas_for_improvement}
                        onChange={e => setManagerReviewData(p => ({ ...p, areas_for_improvement: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Action Items</label>
                      <textarea rows={2} value={managerReviewData.action_items}
                        onChange={e => setManagerReviewData(p => ({ ...p, action_items: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowManagerReviewForm(false)}
                        className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                      <button type="submit"
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700">Submit</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Reviews List */}
          {reviews.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
              {selectedCycle ? 'No reviews for this cycle yet.' : 'Select a cycle or view all reviews.'}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="px-4 py-3">Employee</th>
                      <th className="px-4 py-3">Reviewer</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Self Rating</th>
                      <th className="px-4 py-3">Mgr Rating</th>
                      <th className="px-4 py-3">Overall</th>
                      <th className="px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {reviews.map(r => {
                      const isEmployee = r.employee === currentUser?.id;
                      const isReviewer = r.reviewer === currentUser?.id;
                      return (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-700">{r.employee_name || `User #${r.employee}`}</td>
                          <td className="px-4 py-3 text-gray-600">{r.reviewer_name || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${REVIEW_STATUS_COLORS[r.status] || ''}`}>
                              {r.status_display || r.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">{r.self_rating ? `${r.self_rating}/5` : '—'}</td>
                          <td className="px-4 py-3">{r.manager_rating ? `${r.manager_rating}/5` : '—'}</td>
                          <td className="px-4 py-3">{r.overall_rating ? `${r.overall_rating}/5` : '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2 flex-wrap">
                              <button onClick={() => setShowReviewDetail(r)}
                                className="text-xs text-gray-600 hover:underline">View</button>
                              {isEmployee && r.status === 'PENDING_SELF' && (
                                <button onClick={() => {
                                  setSelectedReview(r);
                                  setSelfAssessmentData({ self_assessment: r.self_assessment || '', self_rating: r.self_rating || '' });
                                  setShowSelfAssessmentForm(true);
                                }} className="text-xs text-blue-600 hover:underline">Self-Assess</button>
                              )}
                              {(isReviewer || canManageCycles) && r.status === 'PENDING_MGR' && (
                                <button onClick={() => {
                                  setSelectedReview(r);
                                  setManagerReviewData({
                                    manager_assessment: r.manager_assessment || '', manager_rating: r.manager_rating || '',
                                    overall_rating: r.overall_rating || '', strengths: r.strengths || '',
                                    areas_for_improvement: r.areas_for_improvement || '', action_items: r.action_items || '',
                                  });
                                  setShowManagerReviewForm(true);
                                }} className="text-xs text-purple-600 hover:underline">Manager Review</button>
                              )}
                              {(isReviewer || canManageCycles) && r.status === 'IN_DISCUSSION' && (
                                <button onClick={() => handleCompleteReview(r.id)}
                                  className="text-xs text-green-600 hover:underline">Complete</button>
                              )}
                              {isEmployee && r.status === 'COMPLETED' && !r.employee_acknowledgement && (
                                <button onClick={() => handleAcknowledgeReview(r.id)}
                                  className="text-xs text-teal-600 hover:underline">Acknowledge</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Review Detail Modal */}
      {showReviewDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Review Details</h3>
                <button onClick={() => setShowReviewDetail(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Employee</p>
                    <p className="text-sm font-medium text-gray-800">{showReviewDetail.employee_name || `User #${showReviewDetail.employee}`}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Reviewer</p>
                    <p className="text-sm font-medium text-gray-800">{showReviewDetail.reviewer_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Status</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${REVIEW_STATUS_COLORS[showReviewDetail.status] || ''}`}>
                      {showReviewDetail.status_display || showReviewDetail.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Cycle</p>
                    <p className="text-sm text-gray-700">{showReviewDetail.review_cycle_name || `Cycle #${showReviewDetail.review_cycle}`}</p>
                  </div>
                </div>
                <hr />
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 uppercase">Self Rating</p>
                    <p className="text-2xl font-bold text-blue-600">{showReviewDetail.self_rating ? `${showReviewDetail.self_rating}/5` : '—'}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 uppercase">Manager Rating</p>
                    <p className="text-2xl font-bold text-purple-600">{showReviewDetail.manager_rating ? `${showReviewDetail.manager_rating}/5` : '—'}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 uppercase">Overall</p>
                    <p className="text-2xl font-bold text-green-600">{showReviewDetail.overall_rating ? `${showReviewDetail.overall_rating}/5` : '—'}</p>
                  </div>
                </div>
                {showReviewDetail.self_assessment && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium mb-1">Self-Assessment</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{showReviewDetail.self_assessment}</p>
                  </div>
                )}
                {showReviewDetail.manager_assessment && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium mb-1">Manager Assessment</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{showReviewDetail.manager_assessment}</p>
                  </div>
                )}
                {showReviewDetail.strengths && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium mb-1">Strengths</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{showReviewDetail.strengths}</p>
                  </div>
                )}
                {showReviewDetail.areas_for_improvement && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium mb-1">Areas for Improvement</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{showReviewDetail.areas_for_improvement}</p>
                  </div>
                )}
                {showReviewDetail.action_items && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium mb-1">Action Items</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{showReviewDetail.action_items}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== GOALS TAB ==================== */}
      {activeSubTab === 'goals' && !isLoading && (
        <>
          {canManageCycles && (
            <div className="mb-4">
              <button onClick={() => { resetGoalForm(); setShowGoalForm(true); }}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition">
                + Set Goal
              </button>
            </div>
          )}

          {showGoalForm && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">{editingGoal ? 'Edit Goal' : 'Set Performance Goal'}</h3>
                  <form onSubmit={handleGoalSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                      <select required value={goalForm.employee}
                        onChange={e => setGoalForm(p => ({ ...p, employee: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full">
                        <option value="">Select employee</option>
                        {staffUsers.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                      <input type="text" required value={goalForm.title}
                        onChange={e => setGoalForm(p => ({ ...p, title: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="e.g. Achieve 150 billable hours" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <select value={goalForm.goal_type}
                          onChange={e => setGoalForm(p => ({ ...p, goal_type: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full">
                          {GOAL_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                        <input type="date" value={goalForm.due_date}
                          onChange={e => setGoalForm(p => ({ ...p, due_date: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Target</label>
                        <input type="number" step="0.01" value={goalForm.target_value}
                          onChange={e => setGoalForm(p => ({ ...p, target_value: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="150" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                        <input type="text" value={goalForm.unit}
                          onChange={e => setGoalForm(p => ({ ...p, unit: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="hours" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Weight</label>
                        <input type="number" step="0.1" min="0" value={goalForm.weight}
                          onChange={e => setGoalForm(p => ({ ...p, weight: e.target.value }))}
                          className="border rounded-lg px-3 py-2 text-sm w-full" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea rows={2} value={goalForm.description}
                        onChange={e => setGoalForm(p => ({ ...p, description: e.target.value }))}
                        className="border rounded-lg px-3 py-2 text-sm w-full" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={resetGoalForm}
                        className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                      <button type="submit"
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700">{editingGoal ? 'Update Goal' : 'Create Goal'}</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {goals.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
              No goals found.
            </div>
          ) : (
            <div className="space-y-3">
              {goals.map(g => {
                const progress = g.progress_pct != null ? g.progress_pct : (g.target_value && g.actual_value
                  ? Math.round((parseFloat(g.actual_value) / parseFloat(g.target_value)) * 100) : null);
                return (
                  <div key={g.id} className="bg-white rounded-xl shadow-sm border p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-gray-800">{g.title}</h4>
                          <span className="text-xs text-gray-500">{g.employee_name}</span>
                        </div>
                        {g.description && <p className="text-xs text-gray-500 mt-1">{g.description}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <select value={g.status} onChange={e => handleGoalStatusUpdate(g.id, e.target.value)}
                          className="text-xs border rounded px-2 py-1">
                          {GOAL_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        {canManageCycles && (
                          <>
                            <button onClick={() => handleEditGoal(g)} className="text-gray-400 hover:text-blue-600 text-xs" title="Edit">✏️</button>
                            <button onClick={() => handleDeleteGoal(g.id)} className="text-red-400 hover:text-red-600 text-xs">🗑️</button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Inline actual value update */}
                    {g.target_value && (
                      <div className="mt-3">
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                          <span>Progress:</span>
                          <input type="number" step="0.01" min="0"
                            className="border rounded px-2 py-1 w-24 text-xs"
                            value={inlineActual[g.id] !== undefined ? inlineActual[g.id] : (g.actual_value || '')}
                            onChange={e => setInlineActual(p => ({ ...p, [g.id]: e.target.value }))}
                            placeholder="0"
                          />
                          <span>/ {g.target_value} {g.unit}</span>
                          {inlineActual[g.id] !== undefined && inlineActual[g.id] !== (g.actual_value || '').toString() && (
                            <button onClick={() => { handleActualValueUpdate(g.id, inlineActual[g.id]); setInlineActual(p => { const n = {...p}; delete n[g.id]; return n; }); }}
                              className="text-xs text-blue-600 hover:underline font-medium">Save</button>
                          )}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className={`h-2 rounded-full transition-all ${progress >= 100 ? 'bg-green-500' : progress >= 50 ? 'bg-blue-500' : 'bg-yellow-500'}`}
                            style={{ width: `${Math.min(progress, 100)}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PerformanceReviews;
